import { useEffect, useRef, useState } from 'react';

const findScrollParent = (node: HTMLElement): HTMLElement | null => {
  let el = node.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
};

/**
 * Tracks whether the nearest scrollable ancestor is actually overflowing, so a
 * table footer can switch between an inline bar and a fixed/floating one.
 * Observes both the table itself (row count changes its height) and the
 * scroll parent (window resizes change its clientHeight).
 */
export const useTableOverflow = <T extends HTMLElement>() => {
  const containerRef = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const scrollParent = findScrollParent(node);
    if (!scrollParent) return;

    const checkOverflow = () =>
      setIsOverflowing(scrollParent.scrollHeight > scrollParent.clientHeight);

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(node);
    resizeObserver.observe(scrollParent);

    return () => resizeObserver.disconnect();
  }, []);

  return { containerRef, isOverflowing };
};
